<template>
  <div class="app-shell" :class="{ 'is-authenticated': loggedIn }">
    <template v-if="loggedIn">
      <div v-if="mobileOpen" class="sidebar-scrim" @click="mobileOpen = false"></div>

      <aside class="sidebar" :class="{ 'is-open': mobileOpen }">
        <router-link class="brand" to="/dashboard" aria-label="CatNip dashboard">
          <span class="brand-mark">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2 9.8 7.1 4.4 5.6l2.8 4.8L3 14l5.6.7L8.2 20l3.8-3.7 3.8 3.7-.4-5.3L21 14l-4.2-3.6 2.8-4.8-5.4 1.5L12 2Z" />
            </svg>
          </span>
          <span class="brand-copy"><strong>CatNip</strong><small>Development</small></span>
        </router-link>

        <div class="sidebar-section-label">Workspace</div>
        <nav class="sidebar-nav" aria-label="Primary navigation">
          <router-link class="sidebar-link" to="/dashboard" exact-active-class="is-active">
            <svg viewBox="0 0 24 24"><path d="M4 13h6V4H4v9Zm0 7h6v-5H4v5Zm10 0h6v-9h-6v9Zm0-16v5h6V4h-6Z" /></svg>
            <span>Overview</span>
          </router-link>
          <router-link class="sidebar-link" to="/dashboard/apps" active-class="is-active">
            <svg viewBox="0 0 24 24"><path d="M4 5a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v10a3 3 0 0 1-3 3H6a2 2 0 0 1-2-2V5Zm3 5h10M7 14h7" /></svg>
            <span>Applications</span>
          </router-link>
          <router-link class="sidebar-link" to="/dashboard/settings" active-class="is-active">
            <svg viewBox="0 0 24 24"><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6 1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z" /></svg>
            <span>Settings</span>
          </router-link>
        </nav>

        <template v-if="isAdmin">
          <div class="sidebar-section-label">Administration</div>
          <nav class="sidebar-nav" aria-label="Admin navigation">
            <router-link class="sidebar-link" to="/admin" exact-active-class="is-active">
              <svg viewBox="0 0 24 24"><path d="M12 3 4 6v5c0 5.1 3.4 8.5 8 10 4.6-1.5 8-4.9 8-10V6l-8-3Z"/><path d="m9 12 2 2 4-4" /></svg>
              <span>Admin center</span>
            </router-link>
            <router-link class="sidebar-link sidebar-link--sub" to="/admin/users" active-class="is-active"><span>Users</span></router-link>
            <router-link class="sidebar-link sidebar-link--sub" to="/admin/apps" active-class="is-active"><span>Applications</span></router-link>
            <router-link class="sidebar-link sidebar-link--sub" to="/admin/licenses" active-class="is-active"><span>Licenses</span></router-link>
          </nav>
        </template>

        <div class="sidebar-footer">
          <div class="profile-chip">
            <span class="avatar">{{ userInitial }}</span>
            <span class="profile-copy">
              <strong>{{ displayName }}</strong>
              <small>{{ isAdmin ? 'Administrator' : 'Member' }}</small>
            </span>
          </div>
          <router-link class="logout-link" to="/dashboard/logout" title="Log out" aria-label="Log out">
            <svg viewBox="0 0 24 24"><path d="M10 17l5-5-5-5M15 12H3M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" /></svg>
          </router-link>
        </div>
      </aside>

      <main class="workspace">
        <header class="topbar">
          <button class="mobile-menu" aria-label="Open menu" @click="mobileOpen = true">
            <svg viewBox="0 0 24 24"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
          </button>
          <div>
            <div class="eyebrow">CatNip Control</div>
            <div class="topbar-title">{{ pageTitle }}</div>
          </div>
          <div class="topbar-status"><span></span> Systems operational</div>
        </header>
        <div class="app-content"><router-view /></div>
      </main>
    </template>

    <template v-else>
      <header class="public-header">
        <router-link class="brand" to="/">
          <span class="brand-mark">
            <svg viewBox="0 0 24 24"><path d="M12 2 9.8 7.1 4.4 5.6l2.8 4.8L3 14l5.6.7L8.2 20l3.8-3.7 3.8 3.7-.4-5.3L21 14l-4.2-3.6 2.8-4.8-5.4 1.5L12 2Z" /></svg>
          </span>
          <span class="brand-copy"><strong>CatNip</strong><small>Development</small></span>
        </router-link>
        <div class="public-actions">
          <router-link class="text-link" to="/register">Create account</router-link>
          <router-link class="btn primary" to="/login">Sign in</router-link>
        </div>
      </header>
      <main class="public-content"><router-view /></main>
    </template>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return { mobileOpen: false }
  },
  computed: {
    loggedIn() { return !!this.$store.state.loggedIn },
    isAdmin() { return Number(this.$store.state.user?.is_admin) === 1 },
    displayName() {
      const user = this.$store.state.user || {}
      return user.display_name || user.username || 'Account'
    },
    userInitial() { return this.displayName.charAt(0).toUpperCase() },
    pageTitle() {
      const names = {
        Dashboard: 'Overview', Apps: 'Applications', ViewApp: 'Application details',
        Settings: 'Account settings', AdminHome: 'Admin center', AdminUsers: 'User management',
        AdminApps: 'Application management', AdminLicenses: 'License management'
      }
      return names[this.$route.name] || 'Dashboard'
    }
  },
  watch: {
    $route() { this.mobileOpen = false }
  }
}
</script>
